import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    ExternalLink,
    GripVertical,
    Loader2,
    PenLine,
    Scale,
} from "lucide-react";
import { toast } from "sonner";
import TahkimAsamaGecisModal from "./TahkimAsamaGecisModal";
import {
    ASAMA_STIL,
    asamaLabel,
    findKanbanItem,
    kartEksikUyari,
    kartVeriOzet,
} from "../../lib/tahkimAsamaConfig";

function TahkimKart({ item, stil, dragging, onDragStart, onDragEnd, tasinabilir }) {
    const ozet = kartVeriOzet(item);
    const eksik = kartEksikUyari(item);
    const kapali = item.tahkim_asamasi === "KAPANDI" || item.dosya_durumu === "KAPATILDI";

    return (
        <div
            draggable={tasinabilir}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            className={`group relative rounded-xl border border-white/10 bg-gradient-to-br from-[#121A2F] to-[#0B0F19]/90 p-3 border-l-4 ${stil?.cardBorder || "border-l-violet-500"} transition-all ${
                tasinabilir
                    ? "cursor-grab active:cursor-grabbing hover:border-violet-400/35 hover:shadow-lg hover:shadow-violet-500/10"
                    : "opacity-75 cursor-not-allowed"
            } ${dragging ? "opacity-40 scale-[0.98] rotate-1" : ""}`}
        >
            <div className="flex items-start gap-2">
                {tasinabilir ? (
                    <GripVertical className="h-4 w-4 text-slate-600 group-hover:text-violet-400 shrink-0 mt-0.5 transition-colors" />
                ) : (
                    <span className="w-4 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-1">
                        <Link
                            to={`/e/tahkim-dosyalari/${item.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-sm font-mono font-semibold text-[#F3E5AB] hover:underline truncate"
                        >
                            {item.dosya_no || `#${item.id}`}
                        </Link>
                        <Link
                            to={`/e/tahkim-dosyalari/${item.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="p-1 rounded-md text-slate-600 hover:text-[#D4AF37] hover:bg-white/5 shrink-0"
                            title="Tahkim Veri Girişi"
                        >
                            <PenLine className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                    <p className="text-[10px] text-slate-400 truncate mt-0.5">
                        {item.muvekkil_ad_soyad || item.basvuru_sahibi || "—"}
                    </p>
                    {ozet.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            {ozet.map((o) => (
                                <span
                                    key={o.key}
                                    className="text-[9px] px-1.5 py-0.5 rounded-md bg-black/30 border border-white/10 text-slate-400 truncate max-w-full"
                                >
                                    {o.text}
                                </span>
                            ))}
                        </div>
                    )}
                    <div className="flex flex-wrap gap-1 mt-1.5">
                        {kapali && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-500/20 text-slate-300">
                                Kapandı
                            </span>
                        )}
                        {eksik.map((e) => (
                            <span
                                key={e}
                                className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-200 border border-amber-500/25"
                            >
                                {e}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function AsamaKolon({ col, dragOver, dragId, onDragOver, onDragLeave, onDrop, onDragStart, onDragEnd }) {
    const stil = ASAMA_STIL[col.key] || ASAMA_STIL.BASVURU;
    const items = col.items || [];
    const isOver = dragOver === col.key;

    return (
        <div
            className={`flex-shrink-0 w-[280px] flex flex-col rounded-2xl border-2 transition-all duration-200 ${stil.border} ${
                isOver ? `ring-2 ${stil.ring} scale-[1.01] shadow-xl` : "shadow-lg shadow-black/20"
            } bg-[#0B0F19]/95 overflow-hidden`}
            onDragOver={(e) => onDragOver(col.key, e)}
            onDragLeave={() => onDragLeave(col.key)}
            onDrop={(e) => onDrop(col.key, e)}
        >
            <div
                className={`relative px-3 py-3 border-b border-white/10 bg-gradient-to-r ${stil.header}`}
            >
                <div className="flex items-center gap-2">
                    <span
                        className={`flex h-7 w-7 items-center justify-center rounded-lg ${stil.dot} text-[11px] font-bold text-black/90 shadow-inner`}
                    >
                        {stil.step}
                    </span>
                    <div className="min-w-0 flex-1">
                        <div className={`text-xs font-semibold truncate ${stil.accent}`}>
                            {col.label || asamaLabel(col.key)}
                        </div>
                        <div className="text-[9px] text-slate-500 uppercase tracking-wider">
                            Sürükle-bırak
                        </div>
                    </div>
                    <span
                        className={`tabular-nums text-sm font-bold ${stil.accent} bg-black/25 px-2 py-0.5 rounded-lg border border-white/10`}
                    >
                        {col.count ?? items.length}
                    </span>
                </div>
                {isOver && (
                    <div className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
                )}
            </div>

            <div
                className={`flex-1 p-2 space-y-2 min-h-[200px] max-h-[68vh] overflow-y-auto transition-colors ${
                    isOver ? "bg-white/[0.03]" : ""
                }`}
            >
                {items.length === 0 ? (
                    <div
                        className={`flex flex-col items-center justify-center py-10 px-3 rounded-xl border-2 border-dashed ${
                            isOver ? "border-violet-400/50 bg-violet-500/5" : "border-white/10"
                        }`}
                    >
                        <Scale className="h-6 w-6 text-slate-700 mb-2" />
                        <p className="text-[10px] text-slate-600 text-center">
                            {isOver ? "Bırakın — aşama geçişi açılır" : "Boş — kart sürükleyin"}
                        </p>
                    </div>
                ) : (
                    items.map((item) => {
                        const kapali =
                            item.tahkim_asamasi === "KAPANDI" || item.dosya_durumu === "KAPATILDI";
                        const tasinabilir = !kapali;
                        return (
                            <TahkimKart
                                key={item.id}
                                item={item}
                                stil={stil}
                                dragging={dragId === item.id}
                                tasinabilir={tasinabilir}
                                onDragStart={(e) => {
                                    if (!tasinabilir) {
                                        e.preventDefault();
                                        return;
                                    }
                                    e.dataTransfer.setData("tahkimId", String(item.id));
                                    e.dataTransfer.effectAllowed = "move";
                                    onDragStart(item.id);
                                }}
                                onDragEnd={onDragEnd}
                            />
                        );
                    })
                )}
            </div>
        </div>
    );
}

export default function TahkimSurukleBirakTahta({
    columns,
    loading,
    onRefresh,
    baslik,
    aciklama,
    veriGirisiLink = "/e/tahkim-dosyalari",
}) {
    const [dragId, setDragId] = useState(null);
    const [dragOver, setDragOver] = useState(null);
    const [modal, setModal] = useState(null);

    const sortedColumns = useMemo(
        () => [...(columns || [])].sort((a, b) => (ASAMA_STIL[a.key]?.step || 99) - (ASAMA_STIL[b.key]?.step || 99)),
        [columns],
    );

    const onDragOver = (key, e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDragOver(key);
    };

    const onDragLeave = (key) => {
        setDragOver((prev) => (prev === key ? null : prev));
    };

    const onDrop = (hedefAsama, e) => {
        e.preventDefault();
        setDragOver(null);
        setDragId(null);
        const id = e.dataTransfer.getData("tahkimId");
        if (!id || !hedefAsama) return;
        const item = findKanbanItem(columns, id);
        if (!item) return;
        if (item.tahkim_asamasi === hedefAsama) {
            toast.message("Dosya zaten bu aşamada");
            return;
        }
        setModal({ dosya: item, hedefAsama });
    };

    if (loading) {
        return (
            <div className="flex justify-center py-20">
                <Loader2 className="h-9 w-9 animate-spin text-violet-400" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-violet-500/25 bg-gradient-to-r from-violet-500/8 via-transparent to-[#D4AF37]/5 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                            <GripVertical className="h-4 w-4 text-violet-400" />
                            {baslik || "Sürükle-bırak aşama tahtası"}
                        </h2>
                        <p className="text-xs text-slate-400 mt-1 max-w-3xl leading-relaxed">
                            {aciklama ||
                                "Tüm aşamalar arasında kart sürükleyin. Geçiş penceresinde Tahkim Veri Girişi alanlarını doldurabilirsiniz — zorunlu değil."}
                        </p>
                    </div>
                    <Link
                        to={veriGirisiLink}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-[#D4AF37]/35 bg-[#D4AF37]/10 text-xs font-semibold text-[#F3E5AB] hover:bg-[#D4AF37]/20 shrink-0"
                    >
                        <PenLine className="h-4 w-4" />
                        Tahkim Veri Girişi
                        <ExternalLink className="h-3 w-3 opacity-60" />
                    </Link>
                </div>
            </div>

            <div className="flex gap-3 overflow-x-auto pb-4 min-h-[440px] snap-x snap-mandatory">
                {sortedColumns.map((col) => (
                    <div key={col.key} className="snap-start">
                        <AsamaKolon
                            col={col}
                            dragOver={dragOver}
                            dragId={dragId}
                            onDragOver={onDragOver}
                            onDragLeave={onDragLeave}
                            onDrop={onDrop}
                            onDragStart={setDragId}
                            onDragEnd={() => {
                                setDragId(null);
                                setDragOver(null);
                            }}
                        />
                    </div>
                ))}
            </div>

            <TahkimAsamaGecisModal
                open={!!modal}
                dosya={modal?.dosya}
                hedefAsama={modal?.hedefAsama}
                onClose={() => setModal(null)}
                onSuccess={() => {
                    toast.success("Aşama ve veri girişi kaydedildi");
                    onRefresh?.();
                }}
            />
        </div>
    );
}
